import { memo, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSelect } from 'src/core/POS/PosSelect';
import { vars } from 'src/core/themes';
import { borderRadius } from 'src/core/themes/legacy/shared/shape';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { useListingAutoPricingSettings } from 'src/modals/ListingDetails/components/useListingAutoPricingSettings';
import { ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  DefaultQuantityFilterOption,
  getMinQuantityOptions,
  safeParseJson,
} from 'src/utils/inventoryUtils';

import {
  ListingPricingInput,
  ListingPricingSettingsCellProps,
} from '../ListingPriceForm.types';
import { useCellDisabled, useIsSubmittingField } from '../useCellState';
import * as styles from './Cell.css';

const CompListingMinQuantityCellRaw: React.FC<
  ListingPricingSettingsCellProps
> = ({ listing, editable, event, isV2Variant = false }) => {
  const { watch, formState, getFieldState, setValue } =
    useFormContext<ListingPricingInput>();
  const rowIndex = watch('rowIndex');

  const {
    compListingQuantityScoreAdjustmentEnabled,
    compListingQuantityScoreAdjustmentOverrideJson,
    canAutoPrice,
    onCompListingChange,
  } = useListingAutoPricingSettings(listing);

  const isDisabled = useCellDisabled(
    listing,
    canAutoPrice,
    editable,
    event.virtId,
    !compListingQuantityScoreAdjustmentEnabled
  );

  const isSubmittingField = useIsSubmittingField(
    'compListingQuantityScoreAdjustmentOverrideJson'
  );

  const quantityFilterValue = useMemo(() => {
    const parsedQuantityOverridesJson = safeParseJson(
      compListingQuantityScoreAdjustmentOverrideJson ?? 'null'
    );
    return parsedQuantityOverridesJson?.quantityFilter;
  }, [compListingQuantityScoreAdjustmentOverrideJson]);

  const onMinQuantityUpdate = useCallback(
    (newValue: string) => {
      const val = parseInt(newValue);
      const defaultQuantityOverridesJson = safeParseJson(
        formState.defaultValues
          ?.compListingQuantityScoreAdjustmentOverrideJson ?? 'null'
      );
      if (val === defaultQuantityOverridesJson?.quantityFilter) {
        return;
      }
      const sanitizedValue = !isNaN(val) ? val : null;

      const quantityOverrideJsonNew = {
        ...defaultQuantityOverridesJson,
        quantityFilter: sanitizedValue,
      };

      onCompListingChange({
        compListingQuantityScoreAdjustmentOverrideJsonNew: JSON.stringify(
          quantityOverrideJsonNew
        ),
      });

      // Only submit if Enter is hit in the box that has changes
      setValue('isSubmittingPricingSettings', true);
      setValue('isSubmittingRowIndex', rowIndex);
      setValue(
        'submittingField',
        'compListingQuantityScoreAdjustmentOverrideJson'
      );
    },
    [
      formState.defaultValues?.compListingQuantityScoreAdjustmentOverrideJson,
      onCompListingChange,
      rowIndex,
      setValue,
    ]
  );

  const content = (
    <PosFormField
      errors={
        getFieldState(
          'compListingQuantityScoreAdjustmentOverrideJson',
          formState
        )?.error?.message
      }
      showErrorsInline
      className={styles.formFieldContainer}
    >
      {isSubmittingField ? (
        <div className="operation-button">
          <RotatingWrapper>
            <ProcessingIcon size={vars.iconSize.m} />
          </RotatingWrapper>
        </div>
      ) : (
        <PosSelect
          className={
            isV2Variant ? styles.formSelectFieldV2 : styles.formSelectField
          }
          style={!isV2Variant ? { width: 'fit-content' } : {}}
          disabled={isDisabled}
          searchable={true}
          // Value must be string for PosSelect
          value={quantityFilterValue?.toString() ?? DefaultQuantityFilterOption}
          valueOptionsContent={getMinQuantityOptions()}
          placeholderText={ContentId.None}
          onChange={onMinQuantityUpdate}
        />
      )}
    </PosFormField>
  );

  return isV2Variant ? (
    content
  ) : (
    <TableCellDiv align="right" showTooltip={false}>
      {content}
    </TableCellDiv>
  );
};

export const CompListingMinQuantityCell = memo<ListingPricingSettingsCellProps>(
  CompListingMinQuantityCellRaw
);
